'use client';

import { PoweredByFabric } from '@florencelabs/app';
import {
  Facebook,
  Instagram,
  Twitter,
  YouTube,
  LinkedIn,
} from '@florencelabs/icons';
import clsx from 'clsx';
import Image from 'next/image';
import { ReactElement, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneNumber } from '@provider-directory/components/phone-number';
import { ClientUIConfig, FooterData, Social } from '@provider-directory/types';
import { useAnalytics } from '../analytics/analytics-provider';

const FABRIC_LINKS = [
  {
    href: 'https://www.fabrichealth.com/contact',
    title: 'fabricFooter.contactUs',
  },
  {
    href: 'https://www.fabrichealth.com/blog/commitment-to-accessibility',
    title: 'fabricFooter.accessibility',
  },
  {
    href: 'https://www.fabrichealth.com/terms-conditions',
    title: 'fabricFooter.terms',
  },
  {
    href: 'https://www.fabrichealth.com/privacy-policy',
    title: 'fabricFooter.privacyPolicy',
  },
] as const;

export const social: Social = [
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/pages/Montefiore-Medical-Center/134978679940133',
    icon: Facebook,
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/montefiorehealthsystem/',
    icon: Instagram,
  },
  { name: 'X', href: 'https://twitter.com/montefiorenyc', icon: Twitter },
  {
    name: 'YouTube',
    href: 'https://www.youtube.com/user/MontefioreMedCenter',
    icon: YouTube,
  },
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/montefiore-health-system',
    icon: LinkedIn,
  },
];

export const footerData: FooterData = {
  title: 'Explore Montefiore',
  linkGroups: [
    {
      title: 'footer.patientsAndVisitors',
      links: [
        { href: '/providers', title: 'footer.findADoctor' },
        {
          href: 'https://www.montefiore.org/locations-directions-parking',
          title: 'footer.locationDirectionsParking',
        },
        {
          href: 'https://www.montefiore.org/information-for-patients',
          title: 'footer.informationForPatients',
        },
        {
          href: 'https://www.montefiore.org/information-for-visitors',
          title: 'footer.informationForVisitors',
        },
        {
          href: 'https://www.montefiore.org/payment-portal',
          title: 'footer.payBillsOnline',
        },
        {
          href: 'https://www.montefiore.org/financial-services',
          title: 'footer.financialServices',
        },
        {
          href: 'https://www.montefiore.org/body.cfm?id=11008',
          title: 'footer.priceEstimate',
        },
        {
          href: 'https://www.montefiore.org/in-the-neighborhood',
          title: 'footer.inTheNeighborhood',
        },
        {
          href: 'https://www.montefiore.org/commitment-to-quality-care',
          title: 'footer.commitmentToQuality',
        },
        {
          href: 'https://www.montefiore.org/ocphevents',
          title: 'footer.events',
        },
      ],
    },
    {
      title: 'footer.research',
      links: [
        {
          href: 'https://www.montefiore.org/research-highlights',
          title: 'footer.researchHighlights',
        },
        {
          href: 'https://einsteinmed.org/home/research.asp',
          title: 'footer.partnershipWithEinstein',
        },
        {
          href: 'https://www.montefiore.org/research-resources-for-professionals',
          title: 'footer.resourcesForProfessionals',
        },
        {
          href: 'https://www.montefiore.org/giving',
          title: 'footer.fundingOpportunities',
        },
      ],
    },
    {
      title: 'footer.education',
      links: [
        {
          href: 'https://www.healthwise.net/montefiore',
          title: 'footer.patientHealthLibrary',
        },
        {
          href: 'https://www.montefiore.org/grandrounds',
          title: 'footer.grandRounds',
        },
        {
          href: 'https://www.montefiore.org/residency-and-fellowship-information',
          title: 'footer.residency',
        },
        {
          href: 'https://www.montefiore.org/info-for-professionals-continuing-medical-education',
          title: 'footer.continuingMedicalEducation',
        },
        {
          href: 'https://www.montefiore.org/nursing-professional-training-programs',
          title: 'footer.nursingProfessionalDev',
        },
        {
          href: 'https://www.montefiore.org/allied-and-other-health-professionals-training',
          title: 'footer.allied',
        },
        {
          href: 'https://www.montefiore.org/info-for-professionals-professional-development',
          title: 'footer.professionalDev',
        },
        {
          href: 'https://www.montefiore.org/info-for-professionals-alumni-relations',
          title: 'footer.alumni',
        },
      ],
    },
    {
      title: 'footer.community',
      links: [
        {
          href: 'https://www.montefiore.org/community-health-and-wellness',
          title: 'footer.healthAndWellness',
        },
        {
          href: 'https://www.montefiore.org/in-the-community',
          title: 'footer.communityRelations',
        },
        {
          href: 'https://www.montefiore.org/community-services-and-resources',
          title: 'footer.communityServices',
        },
        {
          href: 'https://www.montefiore.org/community-how-you-can-help',
          title: 'footer.howYouCanHelp',
        },
        {
          href: 'https://www.montefiore.org/community-reports',
          title: 'footer.communityReports',
        },
      ],
    },
    {
      title: 'footer.aboutUs',
      links: [
        { href: 'https://www.montefiore.org/about', title: 'footer.aboutUs' },
        {
          href: 'https://www.montefiore.org/contact-montefiore',
          title: 'footer.contact',
        },
        {
          href: 'https://www.montefiore.org/clinical-services',
          title: 'footer.clinicalServices',
        },
        {
          href: 'https://www.montefiore.org/manage-your-health-online',
          title: 'footer.manageHealthOnline',
        },
        {
          href: 'https://www.montefiore.org/supportmontefiore',
          title: 'footer.donate',
        },
      ],
    },
  ],
  secondaryLinkGroups: [
    {
      title: null,
      links: [
        {
          href: 'https://www.montefiore.org/sitemap',
          title: 'footer.sitemap',
        },
        {
          href: 'https://www.montefiore.org/for-vendors',
          title: 'footer.vendors',
        },
        {
          href: 'https://www.montefiore.org/compliance-hotline',
          title: 'footer.compliance',
        },
        {
          href: 'https://www.montefiorehealthsystem.org/',
          title: 'footer.montefioreHealthSystem',
        },
        {
          href: 'https://www.montefiore.org/privacy-policy',
          title: 'footer.privacyPractices',
        },
        {
          href: 'https://www.montefiore.org/privacy-policy-web-and-mobile-applications',
          title: 'footer.privacyPolicy',
        },
      ],
    },
  ],
  copyrightTextLine1: 'footer.copyrightTextLine1',
  copyrightTextLine2: 'footer.copyrightTextLine2',
  copyrightAddress: '111 East 210th Street, Bronx, NY 10467',
  copyrightPhone: '718-920-4321',
};

const DefaultFooter = ({
  config,
  className,
}: {
  config: ClientUIConfig;
  className?: string;
}): ReactElement => {
  const footerHeadingId = useId();
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const year = new Date().getFullYear();

  const trackFooterLinkClick = useCallback(
    (linkText: string) => {
      analytics.navigationClickEvent({
        navigationType: 'footer',
        linkType: 'link',
        clickText: linkText,
      });
    },
    [analytics],
  );
  return (
    <footer
      className={clsx('bg-white', className)}
      aria-labelledby={footerHeadingId}
    >
      <h2 id={footerHeadingId} className={'sr-only'}>
        {t('footer.title')}
      </h2>
      <div
        className={
          'mx-auto max-w-7xl px-6 pb-8 pt-12 tablet:px-16 desktop:px-12 desktop:pt-32'
        }
      >
        <div className={'mb-12'}>
          <a href={config.companyUrl} className={'flex h-8 items-center'}>
            <Image
              priority={true}
              src={config.branding.logoUrl}
              alt={t('aria.logoAlt', { orgName: config.name })}
              width={config.branding.logoWidth}
              height={config.branding.logoHeight}
            />
          </a>
        </div>

        <div className={'grid grid-cols-1 gap-8 lgTablet:grid-cols-5'}>
          {footerData.linkGroups.map((currentLinkGroup) => {
            return (
              <div key={currentLinkGroup.title}>
                <h3
                  className={
                    'text-sm mb-6 font-semibold text-gray-900 dark:text-white'
                  }
                >
                  {t(currentLinkGroup.title)}
                </h3>
                <ul className={'mt-6 space-y-4'}>
                  {currentLinkGroup.links.map((currentLink) => {
                    return (
                      <li key={currentLink.title}>
                        <a
                          onClick={() => {
                            trackFooterLinkClick(t(currentLink.title));
                          }}
                          href={currentLink.href}
                          className={
                            'text-sm leading-6 text-gray-700 hover:text-gray-900'
                          }
                        >
                          {t(currentLink.title)}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        <hr
          className={
            'mx-auto my-8 border-gray-200 desktop:my-12 dark:border-gray-700'
          }
        />

        <div className={'mb-12 flex space-x-6'}>
          {social.map((item, itemIdx) => (
            <a
              key={itemIdx}
              href={item.href}
              onClick={() => {
                trackFooterLinkClick(item.name);
              }}
              className={'text-gray-700 hover:text-gray-900'}
            >
              <span className={'sr-only'}>{item.name}</span>
              <item.icon className={'h-6 w-6'} aria-hidden={'true'} />
            </a>
          ))}
        </div>
        <div>
          {footerData.secondaryLinkGroups.map((currentLinkGroup, index) => {
            return (
              <div key={index}>
                {currentLinkGroup.title ? (
                  <h3
                    className={
                      'text-sm mb-6 font-semibold text-gray-900 dark:text-white'
                    }
                  >
                    {t(currentLinkGroup.title)}
                  </h3>
                ) : null}
                <ul className={'space-y-4'}>
                  {currentLinkGroup.links.map((currentLink) => {
                    return (
                      <li key={currentLink.title}>
                        <a
                          href={currentLink.href}
                          onClick={() => {
                            trackFooterLinkClick(t(currentLink.title));
                          }}
                          className={
                            'text-sm leading-6 text-gray-700 hover:text-gray-900'
                          }
                        >
                          {t(currentLink.title)}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        <hr
          className={
            'mx-auto my-8 border-gray-200 desktop:my-12 dark:border-gray-700'
          }
        />

        <div
          className={
            'text-sm flex flex-col gap-6 text-gray-700 desktop:flex-row desktop:gap-8'
          }
        >
          <div>
            <a
              href={'https://fabrichealth.com'}
              onClick={() => {
                trackFooterLinkClick(
                  `${t(footerData.copyrightTextLine1, { year })} ${t(
                    footerData.copyrightTextLine2,
                  )}`,
                );
              }}
              className={'hover:underline'}
            >
              {t(footerData.copyrightTextLine1, { year })}{' '}
              {t(footerData.copyrightTextLine2)}
            </a>
          </div>
          <div>{footerData.copyrightAddress}</div>
          <div>
            <PhoneNumber phone={footerData.copyrightPhone} />
          </div>
        </div>

        <hr
          className={
            'mx-auto my-8 border-gray-200 desktop:mb-8 desktop:mt-12 dark:border-gray-700'
          }
        />

        <div
          className={
            'grid grid-cols-1 items-center gap-6 tablet:mt-0 desktop:grid-cols-5'
          }
        >
          <PoweredByFabric className={'w-fit'} />
          {FABRIC_LINKS.map((link) => {
            return (
              <a
                key={link.title}
                href={link.href}
                onClick={() => {
                  trackFooterLinkClick(t(link.title));
                }}
                className={
                  'text-sm leading-6 text-gray-700 hover:text-gray-900'
                }
              >
                {t(link.title)}
              </a>
            );
          })}
        </div>
      </div>
    </footer>
  );
};

export default DefaultFooter;
